import Lang from '/@/modules/core/app/services/Lang';
import { FinancialAccount } from '/@/modules/financialaccount/models/FinancialAccount';
import { FinancialAccountType } from '/@/modules/financialaccount/constants/FiancialAccountType';
import { AssetType } from '@/modules/asset/enums/AssetType';
import { ResidenceType } from '@/modules/asset/enums/ResidenceType';
import { FinancialAccountSubType } from '@/modules/financialaccount/constants/FinancialAccountSubType';
import { FinancialInstitution } from '@/modules/predefined/models/FinancialInstitution';
import { PrivateStockValueType } from '@/modules/asset/enums/PrivateStockValueType';

// eslint-disable-next-line @typescript-eslint/typedef, @typescript-eslint/no-explicit-any
export const AssetFormMixin: unknown = (superClass: any) =>
  class extends superClass {
    get ownerType(): string {
      return this.contacts?.length > 0
        ? `${this.contacts.length} ${Lang.t('asset.co-owners')}`
        : Lang.t('asset.single-owner');
    }

    get flowDetailCardSubTitle(): string {
      let subTitle: string = '';

      if (this.meta?.location) {
        subTitle += `, ${Lang.t('asset.located-in')} ${this.meta.location}`;
      }
      if (this.dateAcquired) {
        subTitle += `, ${Lang.t('asset.acquired-on')} ${this.dateAcquired}`;
      }
      if (this.usage) {
        subTitle += `, ${this.usage}`;
      }
      if (this.isInherited !== undefined) {
        subTitle += `, ${this.isInherited ? '' : Lang.t('asset.not')} ${Lang.t(
          'asset.part-of-inheritance'
        )}`;
      }

      if (subTitle.startsWith(',')) {
        subTitle = subTitle.slice(1).trim();
      }

      return subTitle;
    }

    setFinancialInstitution(value: FinancialInstitution): void {
      if (!this.financialAccount) {
        this.financialAccount = FinancialAccount.mapModel({});
        this.financialAccount.type = FinancialAccountType.DEPOSITORY;

        if (this.name) {
          this.financialAccount.name = this.name;
        }
      }
      this.financialAccount.financialInstitutionId = value.id;
      this.financialAccount.financialInstitution = value;
    }

    get valueToTheEstateSubLabel(): string {
      if (this.isPensionPlan) {
        return this.hasEstateAccount ? Lang.t('asset.pension-plans-if') : '';
      }
      if (this.isLifeInsurance) {
        return `${Lang.t('asset.beneficiaries-any-left')} ${Lang.t('asset.value-to-estate-0')}`;
      }
      if (this.isIncome) {
        return this.hasAssetWithOwnership
          ? `${Lang.t('asset.deceased-this-income')} ${this.asset.meta.sharePercentage}%`
          : '';
      }
      return '';
    }

    get descriptiveTitle(): string {
      switch (this.type) {
        case AssetType.BANK_ACCOUNT.key:
          return `${this.financialAccount?.financialInstitution?.name} - ${
            this.financialAccount?.name
          } - ${FinancialAccountSubType.getByKey(this.financialAccount?.subType)!.value}`;
        case AssetType.LAND.key:
          return `${this.name ?? AssetType.LAND.value} - ${this.meta.types
            ?.map((type: { value: string }) => type.value)
            .join(', ')}`;
        case AssetType.REAL_ESTATE.key:
          return `${this.name ?? AssetType.REAL_ESTATE.value} - ${
            // @ts-ignore
            ResidenceType.getByKey(this.meta.residenceType)!.value
          }`;
        case AssetType.LIFE_INSURANCE.key:
          return `${this.name ?? AssetType.LIFE_INSURANCE.value} - ${this.meta.provider?.name}`;
        case AssetType.PENSION_PLAN.key:
          return `${this.name ?? AssetType.PENSION_PLAN.value} - ${
            this.meta.pensionPlanProvider?.name
          }`;
        case AssetType.ART_JEWELRY.key:
          return `${this.name ?? AssetType.ART_JEWELRY.value} - ${
            this.meta.precision ?? this.meta.title
          }`;
        default:
          return '';
      }
    }

    get isPrivateStockTotalValueType(): boolean {
      return (
        this.type === AssetType.PRIVATE_STOCK.key &&
        this.meta.valuationType === PrivateStockValueType.TOTAL_VALUE.key
      );
    }

    get isPrivateStockPerShareValueType(): boolean {
      return (
        this.type === AssetType.PRIVATE_STOCK.key &&
        this.meta.valuationType === PrivateStockValueType.PER_SHARE_VALUE.key
      );
    }

    resetPrivateStockMetaForValueType(): void {
      if (this.isPrivateStockTotalValueType) {
        this.meta.vestedStockCount = null;
        this.meta.valuePerShare = null;
        this.meta.strikePricePerShare = null;

        return;
      }

      if (this.isPrivateStockPerShareValueType) {
        this.meta.valueOfBusiness = null;
        this.meta.ownershipPercentage = null;

        return;
      }
    }

    setPrivateStockAtDeathValue(): void {
      if (
        this.isPrivateStockTotalValueType &&
        this.meta.valueOfBusiness &&
        this.meta.ownershipPercentage
      ) {
        const computedValue: number =
          this.meta.valueOfBusiness * this.meta.ownershipPercentage * 0.01;
        this.atDeathValue = Math.round(computedValue * 100) / 100;

        return;
      }

      if (
        this.isPrivateStockPerShareValueType &&
        this.meta.vestedStockCount &&
        this.meta.valuePerShare
      ) {
        const computedValue: number = this.meta.vestedStockCount * this.meta.valuePerShare;
        this.atDeathValue = Math.round(computedValue * 100) / 100;

        return;
      }

      this.atDeathValue = 0;
    }

    setPrivateStockPurchasePrice(): void {
      if (
        this.isPrivateStockPerShareValueType &&
        this.meta.vestedStockCount &&
        this.meta.strikePricePerShare
      ) {
        const computedValue: number = this.meta.vestedStockCount * this.meta.strikePricePerShare;
        this.purchasePrice = Math.round(computedValue * 100) / 100;

        return;
      }

      if (this.isPrivateStockTotalValueType) {
        return;
      }

      this.purchasePrice = 0;
    }

    setCryptocurrencyAtDeathValue(): void {
      if (this.meta.marketValue && this.meta.amountHeld) {
        const computedValue: number = this.meta.marketValue * this.meta.amountHeld;
        this.atDeathValue = Math.round(computedValue * 100) / 100;
      } else {
        this.atDeathValue = 0;
      }
    }

    setCryptocurrencyPurchasePrice(): void {
      if (this.meta.averagePurchasePrice && this.meta.amountHeld) {
        this.purchasePrice = this.meta.averagePurchasePrice * this.meta.amountHeld;
      } else {
        this.purchasePrice = 0;
      }
    }

    setCryptocurrencyValueAtMarriage(): void {
      if (this.meta.valueAtMarriage && this.meta.amountHeld) {
        const computedValue: number = this.meta.valueAtMarriage * this.meta.amountHeld;
        this.valueAtMarriage = Math.round(computedValue * 100) / 100;
      } else {
        this.valueAtMarriage = 0;
      }
    }
  };
